import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Auth Context and Components
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/components/LoginPage";
import Dashboard from "@/components/Dashboard";
import DashboardNav from "@/components/DashboardNav";
import ContractsPage from "@/components/ContractsPage";
import InvoicesPage from "@/components/InvoicesPage";
import ContactReviewPage from "@/components/ContactReviewPage";
import SettingsPage from "@/components/SettingsPage";
import TeamAdminPage from "@/components/TeamAdminPage";
import AdminRoute from "@/components/AdminRoute";
import ForgotPasswordPage from "@/components/ForgotPasswordPage";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import ContractSignPage from "@/components/ContractSignPage";
import InvoicePayPage from "@/components/InvoicePayPage";
import TimesheetViewPage from "@/components/TimesheetViewPage";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { getFriendlyApiError } from "@/lib/publicError";

// Carbon Design System imports
import {
  Header,
  HeaderName,
  HeaderNavigation,
  HeaderMenuItem,
  HeaderGlobalBar,
  HeaderGlobalAction,
  SideNav,
  SideNavItems,
  SideNavLink,
  Button,
  TextInput,
  TextArea,
  Form,
  Tile,
  Content,
  InlineNotification,
  Tag,
  Loading
} from "@carbon/react";

import {
  Analytics,
  IotConnect,
  Email,
  Phone,
  Time,
  Location,
  Menu,
  Close,
  Light,
  Asleep,
  Dashboard as DashboardIcon,
  ArrowRight,
  CheckmarkFilled,
  User,
  Logout,
  Application,
  MachineLearning,
  Flow,
  UserMultiple,
  Chat,
  Launch,
  Document,
  Receipt,
  Settings as SettingsIcon,
} from "@carbon/icons-react";

// Carbon styles
import "@carbon/styles/css/styles.css";

// API configuration — Netlify Functions (MongoDB)
// Flow: Frontend → /api/* → netlify.toml redirects → netlify/functions/*
// Local dev: run `yarn dev` from frontend/ (Netlify Dev on :8888)
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const API = `${API_BASE}/api`;

// Log API configuration for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    API_BASE: API_BASE || '(empty - using relative paths)',
    API: API,
    NODE_ENV: process.env.NODE_ENV,
    tip: API_BASE ? `Using backend at ${API_BASE}` : 'Using Netlify Functions at /api',
  });
}

const isNavActive = (pathname, path) => {
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
};

const AuthLoadingScreen = () => (
  <div className="loading-screen">
    <Loading withOverlay={false} description="Loading..." />
  </div>
);

function scrollToSection(sectionId) {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function HashScrollHandler() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;

    const sectionId = location.hash.replace('#', '');
    const timer = window.setTimeout(() => scrollToSection(sectionId), 100);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  return null;
}

function useSectionNav(onNavigate) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (sectionId) => {
    onNavigate?.();

    if (pathname === '/') {
      scrollToSection(sectionId);
      window.history.replaceState(null, '', `/#${sectionId}`);
      return;
    }

    navigate({ pathname: '/', hash: sectionId });
  };
}

// Navigation Component
const Navigation = ({ onMenuClick, isSideNavOpen }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goToSection = useSectionNav();

  const handleSectionClick = (event, sectionId) => {
    event.preventDefault();
    goToSection(sectionId);
  };

  return (
    <Header aria-label="SenseStack" data-testid="main-navigation">
      <button 
        className="mobile-menu-btn"
        aria-label="Menu" 
        onClick={onMenuClick}
      >
        {isSideNavOpen ? <Close size={20} /> : <Menu size={20} />}
      </button>
      <HeaderName as={Link} to="/" prefix="">
        <IotConnect size={20} style={{ marginRight: '0.5rem' }} />
        SenseStack
      </HeaderName>
      <HeaderNavigation aria-label="SenseStack Navigation" className="header-nav-desktop">
        <HeaderMenuItem as={Link} to="/" isActive={isNavActive(pathname, "/")}>
          Home
        </HeaderMenuItem>
        <HeaderMenuItem href="/#services" onClick={(event) => handleSectionClick(event, 'services')}>
          Services
        </HeaderMenuItem>
        <HeaderMenuItem href="/#products" onClick={(event) => handleSectionClick(event, 'products')}>
          Products
        </HeaderMenuItem>
        <HeaderMenuItem href="/#contact" onClick={(event) => handleSectionClick(event, 'contact')}>
          Contact
        </HeaderMenuItem>
        {isAuthenticated && (
          <>
            <HeaderMenuItem as={Link} to="/dashboard" isActive={isNavActive(pathname, "/dashboard")}>
              Dashboard
            </HeaderMenuItem>
            <HeaderMenuItem as={Link} to="/contracts" isActive={isNavActive(pathname, "/contracts")}>
              Contracts
            </HeaderMenuItem>
            <HeaderMenuItem as={Link} to="/invoices" isActive={isNavActive(pathname, "/invoices")}>
              Invoices
            </HeaderMenuItem>
            {isAdmin && (
              <>
                <HeaderMenuItem as={Link} to="/contact-review" isActive={isNavActive(pathname, "/contact-review")}>
                  Contact inbox
                </HeaderMenuItem>
                <HeaderMenuItem as={Link} to="/team" isActive={isNavActive(pathname, "/team")}>
                  Team
                </HeaderMenuItem>
              </>
            )}
            <HeaderMenuItem as={Link} to="/settings" isActive={isNavActive(pathname, "/settings")}>
              Settings
            </HeaderMenuItem>
          </>
        )}
      </HeaderNavigation>
      <HeaderGlobalBar>
        {isAuthenticated ? (
          <>
            {user?.imageUrl ? (
              <HeaderGlobalAction
                aria-label="Account settings"
                onClick={() => navigate('/settings')}
                className="header-user-avatar-action"
                data-testid="header-avatar-button"
              >
                <img src={user.imageUrl} alt="" className="header-user-avatar" />
              </HeaderGlobalAction>
            ) : (
              <span className="header-user-name">{user?.name}</span>
            )}
            <HeaderGlobalAction
              aria-label="Logout"
              onClick={async () => { await logout(); navigate('/login'); }}
              data-testid="logout-button"
            >
              <Logout size={20} />
            </HeaderGlobalAction>
          </>
        ) : (
          <HeaderGlobalAction
            aria-label="Login"
            onClick={() => navigate('/login')}
            data-testid="login-button"
          >
            <User size={20} />
          </HeaderGlobalAction>
        )}
        <HeaderGlobalAction 
          aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} 
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {isDarkMode ? <Light size={20} /> : <Asleep size={20} />}
        </HeaderGlobalAction>
      </HeaderGlobalBar>
    </Header>
  );
};

// Side Navigation for Mobile
const MobileSideNav = ({ isOpen, onClose }) => {
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goToSection = useSectionNav(onClose);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    onClose();
  };

  const closeAndNavigate = () => onClose();

  const handleSectionClick = (event, sectionId) => {
    event.preventDefault();
    goToSection(sectionId);
  };

  if (!isOpen) return null;
  
  return (
    <div className="mobile-sidenav mobile-sidenav--open">
      <SideNav aria-label="Side navigation" expanded>
        <SideNavItems>
          <SideNavLink
            as={Link}
            to="/"
            renderIcon={IotConnect}
            isActive={isNavActive(pathname, "/")}
            onClick={closeAndNavigate}
          >
            Home
          </SideNavLink>
          <SideNavLink as={Link} to="/#services" renderIcon={Analytics} onClick={(event) => handleSectionClick(event, 'services')}>
            Services
          </SideNavLink>
          <SideNavLink as={Link} to="/#products" renderIcon={Launch} onClick={(event) => handleSectionClick(event, 'products')}>
            Products
          </SideNavLink>
          <SideNavLink as={Link} to="/#contact" renderIcon={Email} onClick={(event) => handleSectionClick(event, 'contact')}>
            Contact
          </SideNavLink>
          {isAuthenticated && (
            <>
              <SideNavLink
                as={Link}
                to="/dashboard"
                renderIcon={DashboardIcon}
                isActive={isNavActive(pathname, "/dashboard")}
                onClick={closeAndNavigate}
              >
                Dashboard
              </SideNavLink>
              <SideNavLink
                as={Link}
                to="/contracts"
                renderIcon={Document}
                isActive={isNavActive(pathname, "/contracts")}
                onClick={closeAndNavigate}
              >
                Contracts
              </SideNavLink>
              <SideNavLink
                as={Link}
                to="/invoices"
                renderIcon={Receipt}
                isActive={isNavActive(pathname, "/invoices")}
                onClick={closeAndNavigate}
              >
                Invoices
              </SideNavLink>
              {isAdmin && (
                <>
                  <SideNavLink
                    as={Link}
                    to="/contact-review"
                    renderIcon={Chat}
                    isActive={isNavActive(pathname, "/contact-review")}
                    onClick={closeAndNavigate}
                  >
                    Contact inbox
                  </SideNavLink>
                  <SideNavLink
                    as={Link}
                    to="/team"
                    renderIcon={UserMultiple}
                    isActive={isNavActive(pathname, "/team")}
                    onClick={closeAndNavigate}
                  >
                    Team
                  </SideNavLink>
                </>
              )}
              <SideNavLink
                as={Link}
                to="/settings"
                renderIcon={SettingsIcon}
                isActive={isNavActive(pathname, "/settings")}
                onClick={closeAndNavigate}
              >
                Settings
              </SideNavLink>
              <SideNavLink renderIcon={Logout} onClick={handleLogout}>
                Logout
              </SideNavLink>
            </>
          )}
          {!isAuthenticated && (
            <SideNavLink as={Link} to="/login" renderIcon={User} onClick={closeAndNavigate}>
              Login
            </SideNavLink>
          )}
        </SideNavItems>
      </SideNav>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="hero-section" data-testid="hero-section">
      <div className="hero-content">
        <Tag type="blue" className="hero-badge">
          Consulting Services
        </Tag>
        
        <h1 className="hero-title">
          Supply Chain Intelligence
          <span className="hero-title--highlight">& IoT Analytics</span>
        </h1>
        
        <p className="hero-description">
          App development, generative AI research, and supply chain intelligence — built and delivered by SenseStack.
        </p>
        
        <div className="hero-actions">
          <Button kind="primary" size="lg" renderIcon={ArrowRight} href="#contact" data-testid="hero-cta-primary">
            Get in Touch
          </Button>
          <Button kind="tertiary" size="lg" href="#services" data-testid="hero-cta-secondary">
            Our Services
          </Button>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Analytics size={32} />,
      title: 'Supply Chain Intelligence',
      description: 'End-to-end visibility across your supply chain with IoT integration, traceability, and real-time operational insights.',
      features: ['IoT & Asset Tracking', 'Traceability', 'Predictive Analytics', 'Compliance Reporting']
    },
    {
      icon: <Application size={32} />,
      title: 'App Development',
      description: 'Custom web and mobile applications tailored to your business — from MVPs to production-ready platforms.',
      features: ['Web & Mobile Apps', 'Cloud Deployment', 'API Integration', 'UX/UI Design']
    },
    {
      icon: <MachineLearning size={32} />,
      title: 'Generative AI Research',
      description: 'Applied AI research and prototyping — grounded models, intelligent workflows, and practical automation.',
      features: ['LLM Integration', 'AI Prototyping', 'Research & RD&E', 'Workflow Automation']
    },
  ];

  return (
    <section id="services" className="section services-section" data-testid="services-section">
      <div className="section-header">
        <Tag type="blue">WHAT WE DO</Tag>
        <h2 className="section-title">Our Services</h2>
        <p className="section-description">
          Consulting and product development across supply chain, apps, and AI.
        </p>
      </div>
      
      <div className="services-grid services-grid--three">
        {services.map((service, index) => (
          <Tile key={index} className="service-tile" data-testid={`service-card-${index}`}>
            <div className="service-icon">
              {service.icon}
            </div>
            <h3 className="service-title">{service.title}</h3>
            <p className="service-description">{service.description}</p>
            <ul className="service-features">
              {service.features.map((feature, idx) => (
                <li key={idx}>
                  <CheckmarkFilled size={16} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </Tile>
        ))}
      </div>
    </section>
  );
};

// Recent Products Section
const ProductsSection = () => {
  const products = [
    {
      name: 'ChillVerify',
      tagline: 'Cold chain monitoring for logistics, supply chains, and food safety.',
      url: 'https://chillverify.com',
      domain: 'chillverify.com',
      logo: '/logos/chillverify.png',
      icon: <CheckmarkFilled size={32} />,
    },
    {
      name: 'Family Sync',
      tagline: 'Keep your family connected and organised',
      url: 'https://familysync.space',
      domain: 'familysync.space',
      logo: '/logos/FamilySync.png',
      icon: <UserMultiple size={32} />,
    },
    {
      name: 'ChillVerify Flow',
      tagline: 'IoT data pipeline for cold chain sensor data — ingest, transform, and route.',
      url: 'https://flow.chillverify.com',
      domain: 'flow.chillverify.com',
      logo: '/logos/chillverify.png',
      icon: <Flow size={32} />,
    },
    {
      name: 'AI Grounded',
      tagline: 'Grounded AI chat — ChatSabi',
      url: 'https://chatsabi.com',
      domain: 'chatsabi.com',
      logo: '/logos/chatsabi.png',
      icon: <Chat size={32} />,
    },
  ];

  return (
    <section id="products" className="section products-section" data-testid="products-section">
      <div className="section-header">
        <Tag type="green">OUR WORK</Tag>
        <h2 className="section-title">Recent Products</h2>
        <p className="section-description">
          Platforms we've built and continue to evolve.
        </p>
      </div>

      <div className="products-grid">
        {products.map((product, index) => (
          <a
            key={index}
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="product-card-link"
            data-testid={`product-card-${index}`}
          >
            <Tile className="product-tile">
              <div className={`product-icon${product.logo ? ' product-icon--logo' : ''}`}>
                {product.logo ? (
                  <img
                    src={product.logo}
                    alt={`${product.name} logo`}
                    className="product-logo"
                  />
                ) : (
                  product.icon
                )}
              </div>
              <h3 className="product-title">{product.name}</h3>
              <p className="product-tagline">{product.tagline}</p>
              <span className="product-domain">
                {product.domain}
                <Launch size={14} />
              </span>
            </Tile>
          </a>
        ))}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Simple math CAPTCHA
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [captchaError, setCaptchaError] = useState('');
  
  // Generate new captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);
  
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: '' });
    setCaptchaError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError('');
    
    // Verify CAPTCHA
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      setCaptchaError('Incorrect answer. Please try again.');
      generateCaptcha();
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const apiUrl = `${API}/contact`;
      console.log('Submitting form to:', apiUrl);
      const response = await axios.post(apiUrl, formData);
      if (response.data) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        generateCaptcha(); // Reset CAPTCHA on success
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(
        getFriendlyApiError(
          error,
          'We could not send your message. Please try again or email inquiry@sensestack.com.au.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <Phone size={20} />, label: 'Phone', value: '+61 (03) 9010 6148' },
    { icon: <Email size={20} />, label: 'Email', value: 'inquiry@sensestack.com.au' },
    { icon: <Time size={20} />, label: 'Hours', value: '9AM - 5PM (Mon - Fri)' },
    { icon: <Location size={20} />, label: 'Location', value: 'Australia' },
  ];

  return (
    <section id="contact" className="section contact-section" data-testid="contact-section">
      <div className="section-header">
        <Tag type="blue">GET STARTED</Tag>
        <h2 className="section-title">Contact Us</h2>
        <p className="section-description">
          Ready to transform your supply chain? Let's talk.
        </p>
      </div>
      
      <div className="contact-container">
        <div className="contact-info">
          <div className="contact-details">
            {contactInfo.map((info, index) => (
              <div key={index} className="contact-item" data-testid={`contact-info-${index}`}>
                <div className="contact-item-icon">{info.icon}</div>
                <div>
                  <span className="contact-item-label">{info.label}</span>
                  <span className="contact-item-value">{info.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <Tile className="contact-form-tile">
          {submitSuccess ? (
            <div className="form-success" data-testid="form-success">
              <CheckmarkFilled size={40} className="success-icon" />
              <h4>Thank you!</h4>
              <p>We'll be in touch shortly.</p>
              <Button kind="tertiary" onClick={() => setSubmitSuccess(false)}>
                Send Another
              </Button>
            </div>
          ) : (
            <Form onSubmit={handleSubmit} data-testid="contact-form">
              {submitError && (
                <InlineNotification
                  kind="error"
                  title="Something went wrong"
                  subtitle={submitError}
                  lowContrast
                  hideCloseButton
                />
              )}
              
              <TextInput
                id="name"
                labelText="Name"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="contact-name-input"
              />
              
              <TextInput
                id="email"
                type="email"
                labelText="Email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="contact-email-input"
              />
              
              <TextInput
                id="phone"
                labelText="Phone"
                placeholder="Your phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="contact-phone-input"
              />
              
              <TextArea
                id="message"
                labelText="Message"
                placeholder="How can we help you?"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                data-testid="contact-message-input"
              />
              
              <div className="captcha-container">
                <label className="captcha-label">
                  Security Check: What is {captcha.num1} + {captcha.num2}?
                </label>
                <TextInput
                  id="captcha"
                  placeholder="Enter your answer"
                  value={captcha.answer}
                  onChange={(e) => setCaptcha({ ...captcha, answer: e.target.value })}
                  required
                  invalid={!!captchaError}
                  invalidText={captchaError}
                  data-testid="contact-captcha-input"
                />
              </div>
              
              <Button 
                type="submit" 
                kind="primary" 
                disabled={isSubmitting}
                className="submit-button"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? <Loading small withOverlay={false} /> : 'Send Message'}
              </Button>
            </Form>
          )}
        </Tile>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <div className="footer-logo">
            <IotConnect size={24} />
            <span>SenseStack</span>
          </div>
          <p className="footer-description">
            App Development · Generative AI Research · Supply Chain Intelligence
          </p>
        </div>
        
        <div className="footer-links-section">
          <ul className="footer-links">
            <li><a href="/">Home</a></li>
            <li><a href="/#services">Services</a></li>
            <li><a href="/#products">Products</a></li>
            <li><a href="/#contact">Contact</a></li>
            {isAuthenticated && (
              <>
                <li><a href="/dashboard">Dashboard</a></li>
                <li><a href="/contracts">Contracts</a></li>
                <li><a href="/invoices">Invoices</a></li>
              </>
            )}
          </ul>
        </div>
        
        <div className="footer-contact-section">
          <ul className="footer-contact">
            <li>+61 (03) 9010 6148</li>
            <li>inquiry@sensestack.com.au</li>
          </ul>
        </div>
      </div>
      
      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} Sense Stack | ABN 14187234379</p>
      </div>
    </footer>
  );
};

// Redirect authenticated users away from login
const GuestRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
};

// Protected Route Component
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
};

// Main Home Component
const Home = () => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);

  return (
    <div className="app-container">
      <Navigation 
        onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)} 
        isSideNavOpen={isSideNavOpen}
      />
      <MobileSideNav isOpen={isSideNavOpen} onClose={() => setIsSideNavOpen(false)} />
      
      <Content className="main-content">
        <HeroSection />
        <ServicesSection />
        <ProductsSection />
        <ContactSection />
        <Footer />
      </Content>
    </div>
  );
};

// Public layout (home, login) with site chrome
const PublicLayout = () => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);

  return (
    <div className="app-container">
      <Navigation
        onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)}
        isSideNavOpen={isSideNavOpen}
      />
      <MobileSideNav isOpen={isSideNavOpen} onClose={() => setIsSideNavOpen(false)} />
      <Content className="main-content main-content--auth">
        <Outlet />
        <Footer />
      </Content>
    </div>
  );
};

// Dashboard Layout for authenticated pages
const DashboardLayout = () => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  
  return (
    <div className="app-container">
      <Navigation 
        onMenuClick={() => setIsSideNavOpen(!isSideNavOpen)} 
        isSideNavOpen={isSideNavOpen}
      />
      <MobileSideNav isOpen={isSideNavOpen} onClose={() => setIsSideNavOpen(false)} />
      
      <Content className="main-content dashboard-content">
        <DashboardNav />
        <Outlet />
        <Footer />
      </Content>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <HashScrollHandler />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route element={<GuestRoute />}>
              <Route element={<PublicLayout />}>
                <Route path="/login/*" element={<LoginPage />} />
                <Route path="/sign-up/*" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Route>
            </Route>
            <Route path="/sign/:token" element={<ContractSignPage />} />
            <Route path="/pay/invoice/:token" element={<InvoicePayPage />} />
            <Route path="/timesheet/:token" element={<TimesheetViewPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/contracts" element={<ContractsPage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route element={<AdminRoute />}>
                  <Route path="/contact-review" element={<ContactReviewPage />} />
                  <Route path="/team" element={<TeamAdminPage />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
